import { DatePicker as AntDatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import styles from "./style.module.scss";
import classNames from "classnames";

const { RangePicker } = AntDatePicker;

const SHARED_PROPS = {
  format: "YYYY-MM-DD HH:mm:ss",
  getPopupContainer: (trigger: HTMLElement) =>
    trigger.parentElement || document.body,
};

// Discriminated Union Props
type SingleProps = {
  type: "single";
  value?: Dayjs | null;
  onChange: (val: Dayjs | null) => void;
  placeholder?: string;
};

type RangeProps = {
  type: "range";
  value?: [Dayjs, Dayjs] | null;
  onChange: (val: [Dayjs, Dayjs] | null) => void;
  placeholder?: [string, string];
  /**
   * Defaults to the picker's parent. Hosts that are themselves popups (a filter
   * dropdown) pass document.body, or the calendar opens on top of them.
   */
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
  onOpenChange?: (open: boolean) => void;
  /** Class on the calendar popup, e.g. to raise it off a modal of the same colour. */
  popupClassName?: string;
};

type DateRangeProps = {
  type: "dateRange";
  value?: [Dayjs, Dayjs] | null;
  onChange: (val: [Dayjs, Dayjs] | null) => void;
  placeholder?: [string, string];
};

type DatePickerProps = SingleProps | RangeProps | DateRangeProps;

function DatePickerBase(props: DatePickerProps) {
  if (props.type === "single") {
    return (
      <AntDatePicker
        {...SHARED_PROPS}
        showTime={{
          format: "HH:mm:ss",
          defaultValue: dayjs("00:00:00", "HH:mm:ss"),
        }}
        value={props.value}
        onChange={(d) => props.onChange(d)}
        placeholder={props.placeholder}
        className={classNames(styles.datePicker, "datePicker")}
      />
    );
  }

  if (props.type === "dateRange") {
    return (
      <RangePicker
        {...SHARED_PROPS}
        format="YYYY-MM-DD"
        value={props.value}
        onChange={(d) =>
          d?.[0] && d?.[1] ? props.onChange([d[0], d[1]]) : props.onChange(null)
        }
        placeholder={props.placeholder}
        className={classNames(styles.rangePicker, "rangePicker")}
      />
    );
  }

  return (
    <RangePicker
      {...SHARED_PROPS}
      getPopupContainer={
        props.getPopupContainer ?? SHARED_PROPS.getPopupContainer
      }
      onOpenChange={props.onOpenChange}
      popupClassName={props.popupClassName}
      showTime={{
        format: "HH:mm:ss",
        defaultValue: [
          dayjs("00:00:00", "HH:mm:ss"),
          dayjs("00:00:00", "HH:mm:ss"),
        ],
      }}
      value={props.value}
      onChange={(d) =>
        d?.[0] && d?.[1] ? props.onChange([d[0], d[1]]) : props.onChange(null)
      }
      placeholder={props.placeholder}
      className={classNames(styles.rangePicker, "rangePicker")}
    />
  );
}

// Exports
export const DateTime = (props: Omit<SingleProps, "type">) => (
  <DatePickerBase
    {...props}
    type="single"
    placeholder={props.placeholder || "Select Date & Time"}
  />
);

export const DateTimeRange = (props: Omit<RangeProps, "type">) => (
  <DatePickerBase
    {...props}
    type="range"
    placeholder={props.placeholder || ["Start Date & Time", "End Date & Time"]}
  />
);

/**
 * Date-only range. Same control as DateTimeRange without the time panel, which makes it
 * roughly half as wide - the difference between a filter bar that fits on one line and
 * one that wraps. The caller decides which end of the day each bound means.
 */
export const DateRange = (props: Omit<DateRangeProps, "type">) => (
  <DatePickerBase
    {...props}
    type="dateRange"
    placeholder={props.placeholder || ["Start Date", "End Date"]}
  />
);
